// Единый поставщик высоты экранной клавиатуры.
//
// 🪤 РАДИ ЧЕГО ЭТО ПЕРЕПИСАНО (27.08.2026).
//
// Источников высоты было два: нативные события сборки и видимое окно. Этого
// не хватает, и не хватает по-разному на каждой платформе:
//
//   • в мини-приложении Телеграма на Андроиде разметочное окно не сжималось
//     вовсе — нижние панели уезжали под клавиатуру;
//   • на iOS 26 есть сбой: после скрытия клавиатуры смещение окна НЕ
//     СБРАСЫВАЕТСЯ, и страница остаётся сдвинутой навсегда;
//   • первое появление клавиатуры всегда запаздывало: высоту узнавали только
//     из события, которое приходит уже после начала движения;
//   • адресная строка браузера меняет ту же величину на десятки точек и
//     выглядела как «клавиатура открылась на 40 точек».
//
// Теперь источник ОДИН, а способов узнать высоту — четыре, по старшинству:
//
//   1. нативные события сборки        — приходят ДО движения, значение точное;
//   2. системный интерфейс браузера   — тоже до движения (Хром и Телеграм-Андроид);
//   3. видимое окно                   — единственное, что работает на iOS;
//   4. предсказание по фокусу         — сразу подставляем последнюю известную
//                                       высоту, не дожидаясь событий.
//
// Величина пишется ПРЯМО в стиль корня страницы, минуя перерисовку: именно
// множественные перерисовки дают мигание нижних панелей.

// 🔴 ДВА ВОПРОСА, А НЕ ОДИН (24.09.2026).
//
// Раньше одна величина отвечала сразу на два вопроса: «открыта ли клавиатура»
// и «на сколько поднять нижние кнопки». Это одно и то же, только пока окно
// страницы НЕ СЖИМАЕТСЯ под клавиатуру. А сжимается оно по-разному:
//
//   • в мини-приложении Телеграма на iOS окно то сжимается, то нет — и расчёт
//     по видимому окну давал «клавиатуры нет», хотя она на экране: меню
//     приложения вставало прямо на клавиатуру;
//   • тут же «угадывание по прошлому разу» подставляло полную высоту — и кнопки
//     поднимались ещё раз поверх уже сжатого окна: язычок улетал за верх экрана;
//   • редактор заметок в сборке для телефона сам переводит окно в режим
//     «сжиматься», а подъём считался по-старому — та же двойная высота.
//
// Теперь раздельно:
//   • «открыта» — класс kb-open на корне: прячет меню приложения и прочее;
//   • «перекрытие» — переменная --kb-h: сколько клавиатура ЗАКРЫВАЕТ снизу
//     от окна страницы. Сжалось окно — перекрытие ноль, поднимать нечего.

/** Меньше этого — не клавиатура, а адресная строка. */
const ПОРОГ_ШУМА_ТОЧЕК = 80;

/** Насколько нижняя панель стоит выше клавиатуры. */
const ОТСТУП_ПАНЕЛИ_ТОЧЕК = 12;

let установлено = false;
/** Последнее ПЕРЕКРЫТИЕ, а не высота клавиатуры: при сжатом окне это ноль. */
let последнееПерекрытие = 0;
/** Сборка для телефона сжимает окно сама (редактор заметок). */
let окноСжимаетсяСамо = false;
/** Высота окна без клавиатуры — от неё видно, что окно сжалось. */
let базоваяВысота = typeof window !== 'undefined' ? window.innerHeight : 0;

function применить(открыта: boolean, перекрытие: number): void {
    const корень = document.documentElement;
    const лифт = открыта && перекрытие >= ПОРОГ_ШУМА_ТОЧЕК ? Math.round(перекрытие) : 0;
    корень.style.setProperty('--kb-h', `${лифт}px`);
    корень.style.setProperty('--dock-gap', `${ОТСТУП_ПАНЕЛИ_ТОЧЕК}px`);
    if (открыта) {
        последнееПерекрытие = лифт;
        корень.style.setProperty('--chat-input-bottom', `calc(${лифт}px + 6px)`);
        корень.classList.add('kb-open');
    } else {
        корень.style.removeProperty('--chat-input-bottom');
        корень.classList.remove('kb-open');
    }
}

/**
 * Сборка для телефона переключила окно в режим «сжиматься под клавиатуру»
 * (или обратно). Зовёт хук редактора — кроме него окно никто не переключает.
 */
export function отметитьСжатиеОкна(сжимается: boolean): void {
    окноСжимаетсяСамо = сжимается;
}

/** Убрать клавиатуру — одна дверь на всё приложение. */
export async function спрятатьКлавиатуру(): Promise<void> {
    try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
            const { Keyboard } = await import('@capacitor/keyboard');
            await Keyboard.hide();
        }
    } catch { /* не сборка для телефона */ }
    try { (window as any).Telegram?.WebApp?.hideKeyboard?.(); } catch { /* старый клиент */ }
    const фокус = document.activeElement as HTMLElement | null;
    if (фокус && естьФокусВПоле(фокус)) фокус.blur();
}

/**
 * Сбой iOS 26: после скрытия клавиатуры смещение видимого окна не
 * возвращается в ноль, а высота остаётся меньше окна страницы. Страница
 * выглядит съехавшей, и никакое событие этого больше не поправит.
 */
function поправитьЗастрявшееОкно(): void {
    const вв = window.visualViewport;
    if (!вв) return;
    if (вв.offsetTop === 0) return;
    if (document.activeElement && естьФокусВПоле(document.activeElement)) return;
    window.scrollTo(0, 0);
    измерить();
}

function естьФокусВПоле(узел: Element | null): boolean {
    if (!узел) return false;
    const имя = узел.tagName;
    if (имя === 'INPUT' || имя === 'TEXTAREA' || имя === 'SELECT') return true;
    return !!(узел as HTMLElement).isContentEditable;
}

/**
 * Посчитать оба ответа по видимому окну.
 *
 * Перекрытие — чем клавиатура закрывает окно страницы снизу. Сжатие — насколько
 * окно стало ниже обычного. Клавиатура открыта, если есть хоть одно из двух и
 * при этом курсор стоит в поле: без курсора клавиатуры не бывает, а окно может
 * «сжаться» и от панели браузера.
 */
function измерить(): void {
    const вв = window.visualViewport;
    const фокус = естьФокусВПоле(document.activeElement);
    if (!фокус) базоваяВысота = Math.max(базоваяВысота, window.innerHeight);
    if (!вв) { применить(false, 0); return; }
    const перекрытие = Math.max(0, window.innerHeight - вв.height - вв.offsetTop);
    const сжатие = Math.max(0, базоваяВысота - вв.height);
    const открыта = фокус && Math.max(перекрытие, сжатие) >= ПОРОГ_ШУМА_ТОЧЕК;
    применить(открыта, перекрытие);
}

// ── Способ 2: системный интерфейс браузера ───────────────────────────────
function поставитьСистемныйИнтерфейс(): boolean {
    const вк: any = (navigator as any).virtualKeyboard;
    if (!вк) return false;
    try {
        // Просим браузер НЕ сжимать разметку самому: геометрию считаем мы.
        вк.overlaysContent = true;
        вк.addEventListener('geometrychange', () => {
            const высота = вк.boundingRect?.height ?? 0;
            применить(высота >= ПОРОГ_ШУМА_ТОЧЕК, высота);
        });
        return true;
    } catch {
        return false;
    }
}

// ── Способ 3: видимое окно ───────────────────────────────────────────────
function поставитьВидимоеОкно(): void {
    const вв = window.visualViewport;
    if (!вв) return;
    вв.addEventListener('resize', измерить);
    вв.addEventListener('scroll', измерить);
    window.addEventListener('resize', измерить);
    window.addEventListener('orientationchange', () => {
        // Поворот меняет «обычную» высоту — забываем старую.
        базоваяВысота = 0;
        setTimeout(измерить, 350);
    });
    измерить();
}

// ── Способ 4: предсказание по фокусу ─────────────────────────────────────
function поставитьПредсказание(нативно: boolean): void {
    document.addEventListener('focusin', (событие) => {
        if (!естьФокусВПоле(событие.target as Element)) return;
        // Не ждём события: клавиатура почти наверняка появится. Подставляем
        // прошлое ПЕРЕКРЫТИЕ (при сжатом окне это ноль, а не высота
        // клавиатуры — отсюда и был двойной подъём).
        if (!нативно) применить(true, последнееПерекрытие);
        setTimeout(измерить, 350);
    });
    document.addEventListener('focusout', () => {
        // Дать браузеру мгновение: перевод фокуса между полями клавиатуру
        // не убирает, и гасить панель на нём было бы миганием.
        setTimeout(() => {
            if (естьФокусВПоле(document.activeElement)) return;
            if (!нативно) измерить();
            поправитьЗастрявшееОкно();
        }, 120);
    });
}

// ── Телеграм ─────────────────────────────────────────────────────────────
function настроитьТелеграм(): void {
    const тг: any = (window as any).Telegram?.WebApp;
    if (!тг) return;
    try {
        тг.ready?.();
        тг.expand?.();
        // 🪤 БЕЗ ЭТОГО СВАЙП ВНУТРИ РЕДАКТОРА СХЛОПЫВАЕТ МИНИ-ПРИЛОЖЕНИЕ:
        // человек ведёт пальцем по тексту, а окно уезжает вниз.
        тг.disableVerticalSwipes?.();
    } catch { /* старый клиент — часть возможностей просто отсутствует */ }
}

/** Мини-приложение Телеграма на iOS: собственный вьюпорт там бесполезен. */
function этоТелеграмНаIOS(): boolean {
    const тг: any = (window as any).Telegram?.WebApp;
    const площадка = String(тг?.platform || '');
    return площадка === 'ios' || площадка === 'macos';
}

export async function installKeyboardSync(): Promise<void> {
    if (установлено) return;
    установлено = true;

    применить(false, 0);
    настроитьТелеграм();
    // 🪤 Скрипт Телеграма вставляется на страницу сам по себе и бывает готов
    // ПОЗЖЕ нас: тогда «развернуть» и «не схлопывать свайпом» молча не
    // включались. Повторяем, когда он точно успел.
    if (!(window as any).Telegram?.WebApp) {
        window.addEventListener('load', настроитьТелеграм, { once: true });
        setTimeout(настроитьТелеграм, 1500);
    }

    try {
        // Ленивый ввоз: веб-сборка работает и без нативного пакета.
        const { Keyboard } = await import('@capacitor/keyboard');
        const { Capacitor } = await import('@capacitor/core');

        if (Capacitor.isNativePlatform()) {
            // Способ 1 — самый точный: значение приходит ДО движения.
            // Открыта/закрыта — только по нативным событиям: угадывание по
            // фокусу здесь врало (кнопка забирала фокус, клавиатура оставалась,
            // а меню приложения вылезало прямо на неё).
            Keyboard.addListener('keyboardWillShow', (сведения) => {
                const высота = сведения?.keyboardHeight ?? 0;
                применить(true, окноСжимаетсяСамо ? 0 : высота);
            });
            Keyboard.addListener('keyboardWillHide', () => применить(false, 0));
            поставитьПредсказание(true);
            return;
        }
    } catch { /* пакета нет — это обычный браузер */ }

    // 🪤 На iOS в Телеграме собственный вьюпорт Телеграма при открытии
    // клавиатуры НЕ МЕНЯЕТСЯ ВООБЩЕ — использовать его там нельзя, работает
    // только видимое окно браузера.
    const системный = этоТелеграмНаIOS() ? false : поставитьСистемныйИнтерфейс();
    if (!системный) поставитьВидимоеОкно();
    поставитьПредсказание(false);

    // Сбой iOS 26 ловим и при возврате на вкладку: клавиатуру могли закрыть,
    // пока страница была в фоне, и события до нас не дошли.
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) поправитьЗастрявшееОкно();
    });
}
